/**
 * Reference catalog of the Ley 23/2015 + D.E. 35/2022 **compliance-function and
 * prevention** obligations of a sujeto obligado — the organizational duties
 * (compliance structure, Manual de Prevención, UAF reporting, staff training,
 * ongoing monitoring, record-keeping, preventive freezing) that sit *around* the
 * per-client due-diligence (DDC) checks the validations engine already covers.
 *
 * This is legal reference data, not tenant data: it is global, public law, and
 * carries no `tenant_id`. It powers the copilot's `explain_compliance_obligations`
 * read tool so an oficial de cumplimiento can ask, in es-PA, "¿qué obligaciones de
 * cumplimiento tengo bajo la Ley 23?" and get a cited answer.
 *
 * Every `extracto` is a VERBATIM cut from the source text under
 * `docs/ley23_fuentes/normas_texto/` (OCR left as-is — do not "correct" it), and
 * every `articulo` was adversarially verified against that source. When you edit
 * this file, keep quotes verbatim and article numbers checked against the source,
 * or the copilot will cite law that does not say what we claim.
 */
/** es-PA label for each pilar de cumplimiento. */
export const LEY23_EJE_LABELS = {
    estructura: 'Estructura y función de cumplimiento',
    manual: 'Manual de Prevención y enfoque basado en riesgo',
    reportes: 'Reportes a la UAF (efectivo, operación sospechosa, confidencialidad)',
    personal: 'Personal: capacitación, conocimiento e idoneidad',
    monitoreo: 'Seguimiento continuo y examen de operaciones',
    registros: 'Conservación de registros y transferencias electrónicas',
    congelamiento: 'Congelamiento preventivo (listas del Consejo de Seguridad de la ONU)',
};
/**
 * True when a `sujeto` filter should surface an obligation tagged `entrySujeto`.
 * 'todos' always matches; a compound tag (e.g. 'financieros y no financieros')
 * matches only its named halves — so filtering by 'profesionales' correctly
 * excludes an obligation whose article never mentions profesionales.
 */
export function ley23SujetoAplica(entrySujeto, filtro) {
    return entrySujeto === 'todos' || entrySujeto.split(' y ').includes(filtro);
}
/**
 * The compliance-function / prevention obligations, grouped by eje. Deduplicated
 * from an 8-eje source sweep; each entry verified verbatim against the source
 * text (Ley 23/2015 and D.E. 35/2022).
 */
export const LEY23_OBLIGATION_REFERENCE = [
    // --- estructura: función de cumplimiento y enlace ---
    {
        eje: 'estructura',
        obligacion: 'Designar una persona o unidad responsable de servir como enlace con la UAF y el respectivo organismo de supervisión.',
        sujeto: 'todos',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 12',
        extracto: 'responsable de servir como enlace con la Unidad de Análisis  Financiero para la Prevención del delito de Blanqueo de Capitales y Financiamiento del Terrorismo y el respectivo organismo de supervisión',
    },
    {
        eje: 'estructura',
        obligacion: 'Establecer las políticas, procedimientos y estructuras de controles internos (estructura de cumplimiento) para prevenir el uso indebido de los servicios.',
        sujeto: 'fiduciarias',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 30',
        extracto: 'Las Empresas Fiduciarias tendrán la obligación de establecer las políticas y procedimientos  y las estructuras de controles internos para prevenir que sus servicios sean utilizados en forma indebida para el delito de blanqueo de capitales',
    },
    {
        eje: 'estructura',
        obligacion: 'Designar o asignar la persona o unidad de enlace con la UAF y el supervisor, y registrarla ante el organismo de supervisión.',
        sujeto: 'todos',
        norma: 'D.E. 35 de 2022',
        articulo: 'art. 5',
        extracto: 'Los sujetos obligados deberán designarse o asignar a una persona o unidad responsable de servir como enlace con la Unidad de Análisis Financiero para la Prevención del delito de Blanqueo de Capitales',
    },
    // --- manual: Manual de Prevención y enfoque basado en riesgo ---
    {
        eje: 'manual',
        obligacion: 'Adoptar un Manual de Prevención de BC/FT/FPADM que implemente políticas, procedimientos y controles internos para minimizar los riesgos identificados en las evaluaciones de riesgo.',
        sujeto: 'todos',
        norma: 'D.E. 35 de 2022',
        articulo: 'art. 8',
        extracto: 'Los sujetos obligados deberán adoptar un Manual de Prevención de blanqueo de capitales, financiamiento del terrorismo y financiamiento de la proliferación de armas de destrucción masiva que les permita implementar políticas,',
    },
    {
        eje: 'manual',
        obligacion: 'Someter las políticas, procedimientos y controles a la aprobación de la alta gerencia (socios, directores o equivalentes), que debe monitorear su implementación y mejorarlos según el riesgo.',
        sujeto: 'todos',
        norma: 'D.E. 35 de 2022',
        articulo: 'art. 8',
        extracto: 'Las políticas, procedimientos y controles establecidos por los sujetos obligados deberán ser aprobados por la alta gerencia, es decir, socios, directores u otros con funciones equivalentes, quienes deberán monitorear la implementación',
    },
    {
        eje: 'manual',
        obligacion: 'Aplicar un enfoque basado en riesgos: evaluar productos y servicios, diseñar controles conforme a la complejidad de las actividades y dotar las áreas de control con herramientas tecnológicas.',
        sujeto: 'financieros y no financieros',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 40',
        extracto: 'Los sujetos obligados financieros y los sujetos obligados no financieros deberán aplicar un enfoque basado en riesgos, lo cual implica una evaluación de los productos y servicios que ofrecen',
    },
    {
        eje: 'manual',
        obligacion: 'Adoptar normas de autoevaluación del grado de riesgo y otras buenas prácticas para la prevención de BC/FT/FPADM.',
        sujeto: 'financieros',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 48',
        extracto: 'Los sujetos obligados financieros deberán adoptar normas de autoevaluación del grado de riesgo y otras buenas prácticas para la prevención del Blanqueo de Capitales',
    },
    {
        eje: 'manual',
        obligacion: 'Incluir en el Manual de Prevención las medidas que permitan la detección oportuna de operaciones inusuales.',
        sujeto: 'todos',
        norma: 'D.E. 35 de 2022',
        articulo: 'art. 23',
        extracto: 'Los sujetos obligados deberán contar en sus manuales de prevención de blanqueo de capitales, el financiamiento del terrorismo y el financiamiento de la proliferación de armas de destrucción masiva, las medidas',
    },
    // --- reportes: UAF (RTE, ROS, confidencialidad) ---
    {
        eje: 'reportes',
        obligacion: 'Reportar a la UAF las transacciones en efectivo o cuasi-efectivo iguales o superiores a B/.10,000 (o su acumulado en el período reglamentario).',
        sujeto: 'financieros y no financieros',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 53',
        extracto: 'Artículo 53. Reporte de Transacciones. Los sujetos obligados financieros y sujetos obligados no financieros que apliquen dentro del período que los reglamentos establezcan, deberán reportar las declaraciones de las siguientes transacciones',
    },
    {
        eje: 'reportes',
        obligacion: 'Comunicar directamente a la UAF cualquier operación sospechosa de BC/FT/FPADM, con independencia del monto.',
        sujeto: 'todos',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 54',
        extracto: 'Artículo 54. Obligación de Reportar una Operación Sospechosa. Los sujetos obligados financieros, los sujetos obligados no financieros y actividades realizadas por profesionales sujetas a supervisión deberán comunicar directamente a la Unidad de Análisis Financiero',
    },
    {
        eje: 'reportes',
        obligacion: 'Remitir el reporte de operación sospechosa a la UAF dentro de los 15 días calendario desde la detección del hecho o de fallas en los controles.',
        sujeto: 'todos',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 54',
        extracto: 'Los reportes deberán ser remitidos a la Unidad de Análisis Financiero dentro de los quince días calendario a partir de la detección del hecho, transacción u operación o fallas en los controles.',
    },
    {
        eje: 'reportes',
        obligacion: 'No revelar al cliente ni a terceros (prohibición de tipping-off) que se ha solicitado o proporcionado información, incluido el envío de un ROS a la UAF.',
        sujeto: 'todos',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 56',
        extracto: 'no podrán hacer de conocimiento del cliente o de terceros que una información le ha sido solicitada o ha sido proporcionada, incluyendo el envío de reportes de operaciones sospechosas a la Unidad de Análisis Financiero',
    },
    {
        eje: 'reportes',
        obligacion: 'Remitir a la UAF el reporte de transacciones en efectivo y cuasi-efectivo según la periodicidad aplicable (regular, ocasional o por Declaración Jurada).',
        sujeto: 'todos',
        norma: 'D.E. 35 de 2022',
        articulo: 'art. 24',
        extracto: 'Artículo 24. Reporte de Transacciones. Los sujetos obligados deberán remitir a la Unidad de Análisis Financiero para la prevención de Blanqueo de Capitales y el Financiamiento del Terrorismo, el reporte de transacciones en efectivo y cuasi-efectivo',
    },
    {
        eje: 'reportes',
        obligacion: 'Reportar inmediatamente a la UAF —y llevar registro de— cualquier operación sospechosa, incluidas las tentativas, tras la gestión de análisis y con independencia del monto.',
        sujeto: 'todos',
        norma: 'D.E. 35 de 2022',
        articulo: 'art. 25',
        extracto: 'Artículo 25. Obligación de reportar una operación sospechosa. Posterior a realizar una gestión para poder considerar si se encuentra ante una operación sospechosa, los sujetos obligados deberán reportar inmediatamente a la Unidad de Análisis Financiero',
    },
    {
        eje: 'reportes',
        obligacion: 'Reportar la operación sospechosa mediante los formularios y procedimientos de la UAF, anexando la mayor cantidad de documentos de soporte y de debida diligencia.',
        sujeto: 'todos',
        norma: 'D.E. 35 de 2022',
        articulo: 'art. 25',
        extracto: 'Reportar inmediatamente la operación sospechosa mediante los formularios diseñados y procedimientos que para tales efectos establezca la Unidad de Análisis Financiero para la Prevención del Delito de Blanqueo de Capitales y Financiamiento del Terrorismo.',
    },
    // --- personal: capacitación, conocimiento e idoneidad ---
    {
        eje: 'personal',
        obligacion: 'Aplicar una política de conocimiento del empleado (KYE): seleccionar y supervisar al personal de cargos sensibles y mantener un perfil del empleado durante la relación laboral.',
        sujeto: 'financieros y no financieros',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 42',
        extracto: 'Los sujetos obligados financieros y sujetos obligados no financieros deberán seleccionar adecuadamente y supervisar la conducta de sus empleados',
    },
    {
        eje: 'personal',
        obligacion: 'Capacitar a los empleados para que entiendan los riesgos a los que están expuestos, los controles que los mitigan y el impacto personal e institucional de sus acciones.',
        sujeto: 'financieros y no financieros',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 42',
        extracto: 'Los empleados deberán ser capacitados para entender los riesgos a los que están expuestos, los controles que mitigan tales riesgos y el impacto personal e institucional por sus acciones.',
    },
    {
        eje: 'personal',
        obligacion: 'Brindar capacitación continua y específica al personal de áreas sensibles (clientes, caja, cumplimiento, riesgo, RRHH, tecnología, auditoría interna) sobre tipologías y regulación de BC/FT/FPADM.',
        sujeto: 'todos',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 47',
        extracto: 'Los sujetos obligados financieros, sujetos obligados no financieros y actividades realizadas por profesionales sujetas a supervisión deberán brindar capacitación continua y específica a los empleados',
    },
    {
        eje: 'personal',
        obligacion: 'Adoptar medidas para mantener la confidencialidad y proteger la identidad de empleados, directivos o agentes que hayan reportado a los órganos internos de prevención.',
        sujeto: 'todos',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 57',
        extracto: 'adoptarán las medidas adecuadas para mantener la confidencialidad sobre la identidad de los empleados, directivos, o agentes  que hayan realizado una comunicación o reporte a los órganos internos de prevención del sujeto obligado.',
    },
    {
        eje: 'personal',
        obligacion: 'Capacitar específica y continuamente a los empleados para que tengan las competencias que les permitan levantar el perfil financiero y transaccional del cliente.',
        sujeto: 'no financieros',
        norma: 'D.E. 35 de 2022',
        articulo: 'art. 9',
        extracto: 'Los sujetos obligados no financieros deberán capacitar específica y continuamente a sus empleados para que tengan las competencias que les permitan levantar el perfil financiero y el perfil transaccional del cliente, según aplique.',
    },
    // --- monitoreo: seguimiento continuo y examen ---
    {
        eje: 'monitoreo',
        obligacion: 'Comprobar las actividades declaradas del cliente cuando concurran las circunstancias del examen especial o cuando las operaciones no correspondan con su actividad, perfil o antecedentes.',
        sujeto: 'financieros',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 38',
        extracto: 'Comprobar las actividades declaradas de sus clientes conforme se establezcan en los reglamentos de esta Ley y, en todo caso, cuando concurran las circunstancias que determinen el examen especial de operaciones que establece el artículo 41',
    },
    {
        eje: 'monitoreo',
        obligacion: 'Dar seguimiento continuado a las operaciones a lo largo de la relación de negocios para garantizar que coincidan con la actividad, perfil financiero y transaccional del cliente.',
        sujeto: 'financieros',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 39',
        extracto: 'Realizar un seguimiento de las operaciones efectuadas a lo largo de la relación de negocios a fin de garantizar que coincidan con la actividad profesional o empresarial del cliente, perfil financiero y transaccional.',
    },
    {
        eje: 'monitoreo',
        obligacion: 'Incrementar el seguimiento cuando se observen señales de alerta o comportamientos de riesgo superiores al promedio, por norma o por el análisis de riesgo propio.',
        sujeto: 'financieros',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 39',
        extracto: 'Los sujetos obligados financieros incrementarán el seguimiento cuando se observen  señales de alerta o comportamientos de riesgos supriores al promedio, por disposición normativa o porque así se desprenda del análisis de riesgo',
    },
    {
        eje: 'monitoreo',
        obligacion: 'Realizar periódicamente procesos de revisión para asegurar que los documentos, datos e información de la debida diligencia se mantengan actualizados.',
        sujeto: 'financieros',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 39',
        extracto: 'Realizar periódicamente procesos de revisión con objeto de asegurar que los documentos, datos e información obtenidos como consecuencia de la aplicación de las medidas de debida diligencia se mantengan actualizadas',
    },
    {
        eje: 'monitoreo',
        obligacion: 'Examinar con especial atención cualquier hecho u operación inusual, sin importar su cuantía, analizando sus antecedentes y propósito y dejando constancia escrita de los hallazgos.',
        sujeto: 'todos',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 41',
        extracto: 'Los sujetos obligados financieros, los sujetos obligados no financieros y actividades realizadas por profesionales sujetas a supervisión deberán examinar con especial atención cualquier hecho, operación o transacción, con independencia de su cuantía',
    },
    {
        eje: 'monitoreo',
        obligacion: 'Dejar constancia en el expediente de cada operación inusual detectada, del responsable de su análisis y de la determinación (sustentada) de reportarla o no como sospechosa.',
        sujeto: 'todos',
        norma: 'D.E. 35 de 2022',
        articulo: 'art. 23',
        extracto: 'Los sujetos obligados deben dejar constancia en el respectivo expediente de cada una de las operaciones inusuales detectadas, del responsable o responsables de su análisis',
    },
    // --- registros: conservación y transferencias ---
    {
        eje: 'registros',
        obligacion: 'Resguardar los registros de las operaciones por un mínimo de cinco años desde el fin de la relación, de modo que permitan reconstruir las operaciones.',
        sujeto: 'financieros',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 29',
        extracto: 'asimismo resguardarán los registros de las operaciones realizadas, por un período mínimo de cinco años, contados a partir de la terminación de la relación, que hagan posible el conocimiento de este y la reconstrucción de las operaciones.',
    },
    {
        eje: 'registros',
        obligacion: 'Resguardar la información y documentación de la debida diligencia en los mismos términos (cinco años, reconstrucción) que los sujetos financieros.',
        sujeto: 'no financieros y profesionales',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 29',
        extracto: 'Los sujetos obligados no financieros y actividades realizadas por profesionales sujetas a supervisión, de igual manera estarán obligados a resguardar la información y documentación en los términos establecidos en el presente artículo.',
    },
    {
        eje: 'registros',
        obligacion: 'Asegurar que las transferencias electrónicas incluyan el nombre del originador, el nombre del beneficiario y un número de cuenta o referencia única de la transacción.',
        sujeto: 'financieros',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 46',
        extracto: 'Los sujetos obligados financieros  deberán asegurar que la información de las transferencias electrónicas incluyan los datos siguientes:',
    },
    {
        eje: 'registros',
        obligacion: 'Mantener los registros de información y documentación del cliente y beneficiario final por un mínimo de cinco años tras terminar la relación.',
        sujeto: 'todos',
        norma: 'D.E. 35 de 2022',
        articulo: 'art. 20',
        extracto: 'La obligación de registro de información y documentación se mantendrá por un período mínimo de cinco años luego de terminada la relación contractual, profesional o de negocios',
    },
    {
        eje: 'registros',
        obligacion: 'Conservar los reportes de transacciones en efectivo o cuasi-efectivo y los documentos que sustentan cada operación por un plazo no menor a cinco años desde el reporte.',
        sujeto: 'todos',
        norma: 'D.E. 35 de 2022',
        articulo: 'art. 24',
        extracto: 'En todo caso, los sujetos obligados conservarán los reportes de transacciones en efectivo o cuasi-efectivo diligenciados y los documentos que sustentan cada transacción u operación',
    },
    {
        eje: 'registros',
        obligacion: 'Mantener actualizados los registros que permitan a las autoridades y al supervisor la reconstrucción de cualquier hecho.',
        sujeto: 'no financieros',
        norma: 'D.E. 35 de 2022',
        articulo: 'art. 21',
        extracto: 'deberán mantener actualizados los registros que permitan a las autoridades competentes y al organismo de supervisión la reconstrucción de cualquier hecho',
    },
    // --- congelamiento preventivo (listas ONU) ---
    {
        eje: 'congelamiento',
        obligacion: 'Efectuar de inmediato un congelamiento preventivo sobre fondos, bienes o activos apenas se reciban las listas del Consejo de Seguridad de la ONU.',
        sujeto: 'todos',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 49',
        extracto: 'Los sujetos obligados deberán proceder  de inmediato a efectuar un congelamiento preventivo sobre los fondos, bienes o activos, una vez recibidas las listas que para tal fin emite el Consejo de Seguridad de las Naciones Unidas',
    },
    {
        eje: 'congelamiento',
        obligacion: 'Al encontrar coincidencia entre la lista y un cliente, suspender toda transacción con este y congelar preventivamente los fondos que posea.',
        sujeto: 'todos',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 49',
        extracto: 'quienes una vez encontrada alguna coincidencia entre la lista y algún cliente procederán a suspender toda transacción con este y a congelar preventivamente los fondos que posea.',
    },
    {
        eje: 'congelamiento',
        obligacion: 'Notificar de inmediato a la UAF que se ha efectuado un congelamiento preventivo sobre los fondos, bienes o activos.',
        sujeto: 'todos',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 49',
        extracto: 'Los sujetos obligados deberán notificar de inmediato a la Unidad de Análisis Financiero que han efectuado un congelamiento preventivo sobre los fondos, bienes o  activos',
    },
    {
        eje: 'congelamiento',
        obligacion: 'No descongelar los bienes y activos hasta recibir la notificación judicial correspondiente.',
        sujeto: 'todos',
        norma: 'Ley 23 de 2015',
        articulo: 'art. 49',
        extracto: 'Los sujetos obligados no descongelarán los bienes y activos hasta no recibir notificación judicial al respecto.',
    },
];
export const LEY23_KEY_DEADLINES = [
    {
        tema: 'Reporte de operación sospechosa (ROS) a la UAF',
        valor: 'De inmediato, tras la gestión de análisis (D.E. 35, art. 25); los 15 días calendario desde la detección del hecho, transacción u operación o de fallas en los controles (Ley 23, art. 54) son el plazo máximo de remisión del reporte a la UAF.',
        articulo: 'Ley 23, art. 54 · D.E. 35, art. 25',
    },
    {
        tema: 'Conservación de registros y expedientes',
        valor: 'mínimo 5 años tras terminar la relación',
        articulo: 'Ley 23, art. 29 · D.E. 35, art. 20',
    },
    {
        tema: 'Umbral de reporte de transacciones en efectivo/cuasi-efectivo (RTE)',
        valor: 'B/.10,000 o su acumulado en el período reglamentario',
        articulo: 'Ley 23, art. 53',
    },
    {
        tema: 'Congelamiento preventivo ante listas de la ONU',
        valor: 'de inmediato al recibir la lista; no descongelar sin orden judicial',
        articulo: 'Ley 23, art. 49',
    },
];
/**
 * Documented GAPS: clauses a user may expect but that are NOT in the Ley 23 /
 * D.E. 35 core text. Surfaced so the copilot can say "eso no está aquí, está en X"
 * instead of hallucinating an article.
 */
export const LEY23_OBLIGATION_GAPS = [
    {
        tema: 'Oficial de Cumplimiento',
        nota: 'La figura del "Oficial de Cumplimiento" NO aparece con ese nombre en la Ley 23/2015 ni en el D.E. 35/2022; el cargo y sus responsabilidades se regulan en las normas sectoriales de cada organismo de supervisión. El equivalente en la Ley 23 es el "enlace" del art. 12.',
    },
];
//# sourceMappingURL=ley23-obligations.js.map